import { organization as baseOrganization } from '../../static-fields'
import type { SEOItemList, SEOOrganization, SEOWebsite } from '../../types'
import { getNavigationList } from '../navigation-list/navigation-list'
import { getWebSite } from '../website/website'

export const JUS_IA_SEO_DEFAULT_BASE_URL = 'https://ia.jusbrasil.com.br' as const

const DEFAULT_ORGANIZATION_DESCRIPTION =
  'Acesse o Jusbrasil para Consulta Processual, Jurisprudência, Legislação, Doutrina, Modelos, Peças Processuais e muito mais.'

const DEFAULT_ORGANIZATION_SAME_AS = [
  'https://www.facebook.com/Jusbrasil/',
  'https://twitter.com/jusbrasil',
  'https://www.instagram.com/jusbrasil/',
  'https://www.youtube.com/jusbrasil',
  'https://www.linkedin.com/company/jusbrasil/',
] as const

const DEFAULT_WEBSITE_NAME = 'Jus IA | Inteligência Artificial do Jusbrasil para Advogados'

const DEFAULT_WEBSITE_DESCRIPTION =
  'Crie Peças, Estratégias e Análises com base em mais de 1.2 bilhões de documentos jurídicos. Conheça já o Jus IA, a Inteligência Artificial do Jusbrasil.'

const DEFAULT_USE_ACTION_NAME = 'Experimentar o Jus IA'

const DEFAULT_NAVIGATION: ReadonlyArray<{ name: string; path: string }> = [
  { name: 'Home', path: '' },
  { name: 'Planos', path: '/planos' },
  { name: 'Sobre', path: '/sobre' },
  { name: 'Estudo', path: '/estudo' },
] as const

export interface JusIaStructuredDataInput {
  baseUrl?: string
  organization?: {
    description?: string
    sameAs?: string[]
  }
  website?: {
    name?: string
    description?: string
    useAction?: {
      id?: string
      name?: string
      target?: string
    }
  }
  navigationItems?: Array<{ name: string; url: string }>
}

const normalizeBaseUrl = (baseUrl: string): string => baseUrl.replace(/\/$/, '')

const resolveUrl = (baseUrl: string, pathOrUrl: string): string => {
  if (pathOrUrl.startsWith('http://') || pathOrUrl.startsWith('https://')) {
    return pathOrUrl
  }
  const path = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`
  return `${baseUrl}${path}`
}

export const getJusIaStructuredDataParts = (
  input: JusIaStructuredDataInput = {}
): {
  entityOrganization: SEOOrganization
  website: SEOWebsite
  siteNavigation: SEOItemList
} => {
  const baseUrl = normalizeBaseUrl(input.baseUrl ?? JUS_IA_SEO_DEFAULT_BASE_URL)
  const orgDescription = input.organization?.description ?? DEFAULT_ORGANIZATION_DESCRIPTION
  const sameAs = [...(input.organization?.sameAs ?? DEFAULT_ORGANIZATION_SAME_AS)]

  const websiteName = input.website?.name ?? DEFAULT_WEBSITE_NAME
  const websiteDescription = input.website?.description ?? DEFAULT_WEBSITE_DESCRIPTION
  const useActionId = input.website?.useAction?.id ?? `${baseUrl}/#use-action`
  const useActionName = input.website?.useAction?.name ?? DEFAULT_USE_ACTION_NAME
  const useActionTarget = resolveUrl(baseUrl, input.website?.useAction?.target ?? '/planos')

  const entityOrganization: SEOOrganization = {
    ...baseOrganization,
    description: orgDescription,
    sameAs,
  }

  const website = getWebSite({
    canonicalUrl: baseUrl,
    name: websiteName,
    description: websiteDescription,
    potentialAction: {
      '@id': useActionId,
      '@type': 'UseAction',
      name: useActionName,
      target: useActionTarget,
    },
  })

  const itemListElement =
    input.navigationItems ??
    DEFAULT_NAVIGATION.map(({ name, path }) => ({
      name,
      url: path === '' ? baseUrl : resolveUrl(baseUrl, path),
    }))

  const siteNavigation = getNavigationList({
    canonicalUrl: baseUrl,
    itemListElement,
  })

  return { entityOrganization, website, siteNavigation }
}
